import { useEffect, useState } from "react";
// Every theme's CSS variables, generated from ./themes.ts at build time by the
// wov-themes plugin in vite.config.ts. Static CSS: switching theme only flips
// an attribute on <html>.
import "virtual:wov-themes.css";
import {
  DEFAULT_THEME_ID,
  THEMES,
  isThemeId,
  migrateLegacyThemeId,
  migrateThemeId,
  type ThemeId,
} from "./themes.js";

/* Theme selection
   ---------------------------------------------------------------------------
   The five Waki family themes (Mac, Boardroom, Graphite, Civic, Cobalt), each
   with a light and a dark mode. The theme is chosen here; light vs dark is
   chosen independently by hooks/useTheme.ts. The two are persisted separately.

   Stored under its own key since the family themes (2026-10-02): the old key
   held Slate, Graphite or Paper, and the old "graphite" (grey) is not the new
   "graphite" (warm), so an old value is migrated once (Slate to Boardroom,
   Paper to Graphite, Graphite to Mac) and written under the new key.

   The chosen id lands on <html> as `data-waki-theme`, which selects that
   theme's variables. public/boot/theme-boot.js sets the same attribute before
   first paint, so a reload never flashes the default theme. */

export type VisualTheme = ThemeId;

export interface ThemeMeta {
  id: VisualTheme;
  name: string;
  description: string;
}

export const AVAILABLE_THEMES: ThemeMeta[] = THEMES.map(({ id, name, description }) => ({ id, name, description }));

const STORAGE_KEY = "wov:theme";
/** Before the family themes: Slate, Graphite or Paper (or an id from the old
 *  37-theme catalog). Read once, migrated, left in place. */
const LEGACY_KEY = "bv2:visual-theme";
const ATTR = "data-waki-theme";

/** The saved theme; on first run after the family themes, the old one
 *  migrated and written under the new key so the boot script applies it. */
function readSaved(): VisualTheme {
  try {
    if (typeof localStorage === "undefined") return DEFAULT_THEME_ID;
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored !== null) {
      const id = migrateThemeId(stored);
      if (id !== stored) localStorage.setItem(STORAGE_KEY, id);
      return id;
    }
    const legacy = localStorage.getItem(LEGACY_KEY);
    const id = legacy !== null ? migrateLegacyThemeId(legacy) : DEFAULT_THEME_ID;
    if (legacy !== null) localStorage.setItem(STORAGE_KEY, id);
    return id;
  } catch {
    /* private mode etc */
    return DEFAULT_THEME_ID;
  }
}

let current: VisualTheme = readSaved();
const listeners = new Set<(t: VisualTheme) => void>();

function applyToDocument(theme: VisualTheme) {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute(ATTR, theme);
}

applyToDocument(current);

export function getTheme(): VisualTheme {
  return current;
}

export function setTheme(theme: VisualTheme) {
  if (!isThemeId(theme)) return;
  current = theme;
  try {
    if (typeof localStorage !== "undefined") localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* ignore */
  }
  applyToDocument(theme);
  for (const l of listeners) l(theme);
}

/** Subscribe to theme changes from React. */
export function useVisualTheme(): {
  theme: VisualTheme;
  setTheme: (t: VisualTheme) => void;
} {
  const [theme, setLocal] = useState<VisualTheme>(current);
  useEffect(() => {
    const fn = (t: VisualTheme) => setLocal(t);
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  }, []);
  return { theme, setTheme };
}
